// Sign-in landing for unauthenticated visitors. One card, two tabs:
// "I'm a parent" (email + password) and "I'm a learner" (username + password).
// A magic-link secondary path and a sign-up link round it out.
// On success we refresh /auth/me and the locale gate swaps in the shell.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Alert, BrandLockup, Button, Field, Input, Segmented } from '@fajrarabi/ui';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { LangSwitcher } from './LangSwitcher';

import { ApiError } from '~/lib/api';
import { getMe, loginWithEmail, loginWithUsername, requestMagicLink, useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { adminOrigin, isStaffOnly, safeRedirect } from '~/lib/sso';

type Tab = 'parent' | 'kid';

export function SignInLanding() {
  const t = useT();
  const { isRtl } = useLocale();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const { refresh } = useAuth();

  const [tab, setTab] = useState<Tab>('parent');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (tab === 'parent') {
        await loginWithEmail(identifier, password);
      } else {
        await loginWithUsername(identifier, password);
      }
      // SSO routing (M1): honor an explicit ?redirect= (e.g. the admin
      // console bounced us here), and send staff-only accounts to admin —
      // the domain-wide cookie means they're already signed in over there.
      const redirectTo = safeRedirect(new URLSearchParams(window.location.search).get('redirect'));
      if (redirectTo) {
        window.location.assign(redirectTo);
        return;
      }
      const me = await getMe();
      if (isStaffOnly(me.roles)) {
        window.location.assign(adminOrigin());
        return;
      }
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError ? t('app.auth.sign_in_failed') : t('app.common.error_generic'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMagicLink() {
    setError(null);
    setSubmitting(true);
    try {
      await requestMagicLink(identifier);
      setMagicSent(true);
    } catch {
      setError(t('app.common.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-cream-50">
      <header className="flex items-center justify-between px-6 py-5">
        <BrandLockup label={t('app.common.app_name')} />
        <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
      </header>

      <main className="grid place-items-center px-6 pb-16 pt-6">
        <div className="w-full max-w-md">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-white p-8 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <h1 className="text-2xl font-bold text-ink-900">{t('app.auth.welcome_title')}</h1>
            <p className="mt-1 text-sm text-ink-700">{t('app.auth.welcome_subtitle')}</p>

            <div className="mt-6">
              <Segmented
                ariaLabel={t('app.auth.welcome_title')}
                value={tab}
                onChange={(next) => {
                  setTab(next);
                  setIdentifier('');
                  setError(null);
                  setMagicSent(false);
                }}
                options={[
                  { value: 'parent', label: t('app.auth.tab_parent') },
                  { value: 'kid', label: t('app.auth.tab_kid') },
                ]}
              />
            </div>

            <div className="mt-5 space-y-4">
              {tab === 'parent' ? (
                <Field label={t('app.auth.email_label')}>
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      required
                      autoComplete="email"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                    />
                  )}
                </Field>
              ) : (
                <Field label={t('app.auth.username_label')}>
                  {(id) => (
                    <Input
                      id={id}
                      type="text"
                      required
                      autoComplete="username"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                    />
                  )}
                </Field>
              )}

              <Field label={t('app.auth.password_label')}>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
            </div>

            {error && (
              <Alert tone="danger" className="mt-4">
                {error}
              </Alert>
            )}

            {magicSent ? (
              <Alert tone="info" className="mt-6">
                {t('app.auth.magic_sent')}
              </Alert>
            ) : (
              <Button type="submit" loading={submitting} fullWidth className="mt-6">
                {t('app.auth.sign_in')}
              </Button>
            )}

            {tab === 'parent' && !magicSent && (
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={submitting || identifier.length === 0}
                className="mt-4 block w-full text-center text-sm font-medium text-burgundy-500 hover:text-burgundy-600 disabled:opacity-40"
              >
                {t('app.auth.magic_link')}
              </button>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-ink-700">
            {t('app.auth.no_account')}{' '}
            <Link
              to="/$locale/signup"
              params={{ locale }}
              className="font-semibold text-burgundy-500 hover:text-burgundy-600"
            >
              {t('app.auth.sign_up_link')}
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-ink-500">
            <Link
              to="/$locale/school-signup"
              params={{ locale }}
              className="font-medium text-ink-700 hover:text-burgundy-600"
            >
              {t('app.school.signup_title')}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
